import {
  ConfettiIcon,
  InfoIcon,
  RocketLaunchIcon,
  SpinnerGapIcon,
} from '@phosphor-icons/react';
import { Alert } from '@whistler-ui/k2/components/Alert';
import { Button } from '@whistler-ui/k2/components/Button';
import { Modal } from '@whistler-ui/k2/components/Modal';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import { tryCatch } from '@whistler-ui/k2/helpers';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganisationUpgrade } from '../../hooks';
import { useAuthStore } from '../../store';
import { useThemeStore } from '../../store/theme';
import { OrganisationStatus } from '../../types/enums';

const componentName = 'c-UpgradeRequestModal';

type Step = 'initial' | 'loading' | 'confirmation';

interface Props {
  show: boolean;
  onClose: () => void;
}

export const UpgradeRequestModal = ({ show, onClose }: Props) => {
  const { t } = useTranslation();
  const [setMessage] = useAppMessages(t);
  const { colors } = useThemeStore();

  const {
    authValues: {
      organisationState: { organisation },
    },
    setOrganisationValues,
  } = useAuthStore();

  const isAwaitingApproval =
    organisation?.status === OrganisationStatus.AwaitingProductionApproval;

  const [step, setStep] = useState<Step>('initial');

  // Initialize the step ONLY when the modal opens. isAwaitingApproval must
  // not be a dependency: a background organisation refetch can briefly write
  // a stale (pre-submit) status into the store, and re-running this effect
  // then would knock an in-flight/confirmed modal back to the initial step.
  useEffect(() => {
    if (show) {
      setStep(isAwaitingApproval ? 'confirmation' : 'initial');
    }
  }, [show]);

  const { mutateAsync: upgradeOrganisation } = useOrganisationUpgrade();

  const handleSubmit = async () => {
    setStep('loading');

    const { error } = await tryCatch(async () => {
      return await upgradeOrganisation();
    });

    if (error) {
      setMessage({
        type: useAppMessages.type.ERROR,
        msg: getUserFacingErrorMessage(error, 'common:errors.default'),
      });
      setStep('initial');
      return;
    }

    if (organisation) {
      setOrganisationValues({
        ...organisation,
        status: OrganisationStatus.AwaitingProductionApproval,
      });
    }

    setStep('confirmation');
  };

  const renderIcon = (icon: ReactNode) => {
    return (
      <div
        className="flex items-center justify-center rounded-full size-20"
        style={{ backgroundColor: `${colors.colorBrandPrimary}14` }}
      >
        {icon}
      </div>
    );
  };

  const renderInitial = () => {
    return (
      <Stack direction="column" gap={8} align="center">
        {renderIcon(<RocketLaunchIcon size={48} />)}

        <Stack
          direction="column"
          gap={2}
          align="center"
          className="text-center"
        >
          <Heading level={1} tag="h2">
            {t('upgradeRequestModal_title')}
          </Heading>
          <Paragraph align="center">
            {t('upgradeRequestModal_subtitle')}
          </Paragraph>
        </Stack>

        <Alert
          variant="info"
          title={t('upgradeRequestModal_info_title')}
          customIcon={<InfoIcon size={16} weight="bold" />}
          showCopyButton={false}
          className="w-full"
          style={{ '--default': 'var(--informative)' } as CSSProperties}
        >
          <ul className="pl-0 list-disc list-inside">
            <li>{t('upgradeRequestModal_info_step_1')}</li>
            <li>{t('upgradeRequestModal_info_step_2')}</li>
            <li>{t('upgradeRequestModal_info_step_3')}</li>
          </ul>
        </Alert>

        <Stack direction="column" gap={4} className="w-full">
          <Stack direction="row" gap={3} justify="center" className="w-full">
            <Button
              fluid
              type="button"
              color="tertiary"
              classes={{
                button: 'justify-center',
              }}
              id={`${componentName}__cancel-button`}
              onClick={onClose}
            >
              {t('upgradeRequestModal_button_cancel')}
            </Button>
            <Button
              fluid
              type="button"
              classes={{
                button: 'justify-center',
              }}
              id={`${componentName}__submit-button`}
              onClick={handleSubmit}
            >
              {t('upgradeRequestModal_button_submit')}
            </Button>
          </Stack>
          <Paragraph size="sm" align="center">
            {t('upgradeRequestModal_footnote')}
          </Paragraph>
        </Stack>
      </Stack>
    );
  };

  const renderLoading = () => {
    return (
      <Stack direction="column" gap={8} align="center" justify="center">
        {renderIcon(<SpinnerGapIcon size={48} className="animate-spin" />)}
        <Heading level={1} tag="h2">
          {t('upgradeRequestModal_loading_title')}
        </Heading>
      </Stack>
    );
  };

  const renderConfirmation = () => {
    return (
      <Stack direction="column" gap={8} align="center">
        {renderIcon(<ConfettiIcon size={48} />)}

        <Stack
          direction="column"
          gap={2}
          align="center"
          className="text-center"
        >
          <Heading level={1} tag="h2">
            {t('upgradeRequestModal_confirmation_title')}
          </Heading>
          <Paragraph align="center">
            {t('upgradeRequestModal_confirmation_subtitle')}
          </Paragraph>
        </Stack>

        <Button
          type="button"
          id={`${componentName}__close-button`}
          onClick={onClose}
        >
          {t('upgradeRequestModal_button_close')}
        </Button>
      </Stack>
    );
  };

  const renderStep = () => {
    if (step === 'loading') {
      return renderLoading();
    }

    if (step === 'confirmation') {
      return renderConfirmation();
    }

    return renderInitial();
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      showBtnClose={false}
      size="large"
      classNames={{
        content: 'rounded-3xl',
      }}
    >
      <div className={`${componentName} py-6`} data-testid={componentName}>
        {renderStep()}
      </div>
    </Modal>
  );
};
